import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  BudgetSpend,
  ButtonUI,
  Duration,
  InputSearch,
  ModalUI,
  PacingStatus,
  Status,
  Tag,
  TaptapList,
  useQueryFetch
} from 'sonata-react-components';
import { getEntityDiagnostic, getLineItemStatus, getLineItemsCalendarInfo } from 'sonata-static-storage/lib/entities';

import Subtitle from '@/components/campaigns/ListComponents/Subtitle/Subtitle';
import { useDriveToStoreNavigation } from '@/components/driveToStore/common/hooks/useDriveToStoreNavigation';
import SelectionToolbar from '@/components/driveToStore/common/SelectionToolbar/SelectionToolbar';
import { normalizeCalendarInfo } from '@/helpers/common/date.helper';
import { getAggregatedFootfallLineItemData } from '@/services/footfall.service';
import { getLineItems } from '@/services/lineItem.service';
import { useGlobalStore } from '@/store/global';
import { getBaseCurrency } from '@/store/global/baseCurrency/selectors';

import type { MultiFlightEntityState } from 'sonata-static-storage/lib/common/common.types';
import type { LineItem } from 'sonata-static-storage/lib/entities';
import type { AggregatedFootfallLineItemDataResponse } from '@/services/footfall.service';

import './lineItemSelectionModal.scss';

type ModalHandler = {
  closeModal: () => void;
  openModal: () => void;
};

type Props = {
  entityType: string;
  entityId: string;
  country?: string;
  userId: string;
  isOpen: boolean;
  onClose: () => void;
};

type LineItemRowData = AggregatedFootfallLineItemDataResponse['lineItemData']['lineItems'][number];

type State = {
  searchText: string;
  selectedIds: string[];
  userHasInteracted: boolean;
};

const INITIAL_STATE: State = {
  searchText: '',
  selectedIds: [],
  userHasInteracted: false
};

type LineItemStatusResult = { paused?: boolean; state: unknown };

type LineItemTableRowProps = {
  currencyInfo: ReturnType<typeof getBaseCurrency>;
  fullLineItem?: LineItem;
  lineItem: LineItemRowData;
  userId: string;
};

function LineItemTableRow({ currencyInfo, fullLineItem, lineItem, userId }: Readonly<LineItemTableRowProps>) {
  const { id, name, campaignPlanName, ioName, poiCount } = lineItem;

  const getState = useCallback(async () => {
    const result = (await getLineItemStatus(id)) as unknown as LineItemStatusResult;
    return { state: (result.paused ? 'paused' : result.state) as MultiFlightEntityState };
  }, [id]);

  const getCalendarInfo = useCallback(
    () => getLineItemsCalendarInfo(userId, id).then(normalizeCalendarInfo),
    [userId, id]
  );

  const entityDiagnosticPromise = useMemo(() => getEntityDiagnostic(userId, id, 'lineItem'), [userId, id]);
  const getBudget = useCallback(() => entityDiagnosticPromise, [entityDiagnosticPromise]);
  const getPacing = useCallback(() => entityDiagnosticPromise, [entityDiagnosticPromise]);

  // Line items do not support sync via the status button.
  const handleSyncButtonClick = useCallback(async () => undefined, []);

  const pricingModel = fullLineItem?.bidModels[0]?.bid.pricingModel?.toUpperCase() || '-';
  const lastUpdateDate = fullLineItem
    ? moment.utc((fullLineItem.modifiedDate || fullLineItem.created) as string | undefined).format('DD/MM/YYYY')
    : '-';

  return (
    <TaptapList.RowsList.Row id={id} key={id}>
      <PacingStatus entityId={id} getPacing={getPacing} isInDraft={false} />
      <div className="lineItemSelectionModal__colName">
        <p title={name}>{name}</p>
        <Subtitle campaignPlanName={campaignPlanName ?? 'N/A'} ioName={ioName ?? 'N/A'} type="lineItem" />
      </div>
      <div className="lineItemSelectionModal__colPoiCount">
        <Tag id={`entity-${id}-budget`} status="category">
          {poiCount ?? 0}
        </Tag>
      </div>
      <div className="lineItemSelectionModal__colStatus">
        <Status entityId={id} getState={getState} onSyncButtonClick={handleSyncButtonClick} queryKey={[id]} />
      </div>
      <span className="lineItemSelectionModal__colPricingModel">{pricingModel}</span>
      <div className="lineItemSelectionModal__colDuration">
        <Duration entityId={id} getCalendarInfo={getCalendarInfo} queryKey={[id]} />
      </div>
      <span className="lineItemSelectionModal__colLastUpdate">{lastUpdateDate}</span>
      <div className="lineItemSelectionModal__colBudgetSpend">
        <BudgetSpend currencyInfo={currencyInfo} entityId={id} getBudget={getBudget} isInDraft={false} />
      </div>
    </TaptapList.RowsList.Row>
  );
}

export default function LineItemSelectionModal({
  entityType,
  entityId,
  country,
  userId,
  isOpen,
  onClose
}: Readonly<Props>) {
  const modalRef = useRef<ModalHandler>(null);
  const [{ searchText, selectedIds, userHasInteracted }, setState] = useState<State>(INITIAL_STATE);
  const { formatMessage } = useIntl();
  const { activeEntityIds, navigateToReport } = useDriveToStoreNavigation();
  const currencyInfo = useGlobalStore(getBaseCurrency);

  const { data: lineItemData } = useQueryFetch<AggregatedFootfallLineItemDataResponse | null>(
    ['footfall-lineItemData', entityType, entityId, country],
    async () => getAggregatedFootfallLineItemData(entityType, entityId, userId, country, { includePoiCount: true }),
    { enabled: isOpen }
  );

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.openModal();
      setState(INITIAL_STATE);
    } else {
      modalRef.current?.closeModal();
    }
  }, [isOpen]);

  const lineItems = lineItemData?.lineItemData.lineItems ?? [];
  const lineItemIds = useMemo(() => lineItems.map(li => li.id), [lineItems]);

  const { data: fullLineItemsData } = useQueryFetch<{ items: LineItem[] }>(
    ['lineItems-full-data', lineItemIds.join(',')],
    async () => getLineItems(userId, { ids: lineItemIds }),
    { enabled: isOpen && lineItemIds.length > 0 }
  );

  const fullLineItemById = useMemo(
    () => new Map((fullLineItemsData?.items ?? []).map(li => [String(li._id), li])),
    [fullLineItemsData]
  );

  const defaultSelectedIds = useMemo(() => {
    if (!lineItems.length) {
      return [];
    }
    if (activeEntityIds?.length) {
      const activeSet = new Set(activeEntityIds);
      return lineItems.filter(li => activeSet.has(li.id)).map(li => li.id);
    }
    return lineItems.map(li => li.id);
  }, [lineItems, activeEntityIds]);

  const effectiveSelectedIds = userHasInteracted ? selectedIds : defaultSelectedIds;
  const selectedIdSet = useMemo(() => new Set(effectiveSelectedIds), [effectiveSelectedIds]);

  const filteredLineItems = useMemo(
    () => lineItems.filter(li => !searchText || li.name.toLowerCase().includes(searchText.toLowerCase())),
    [lineItems, searchText]
  );

  const defaultSelectedRows = useMemo(
    () => filteredLineItems.filter(li => selectedIdSet.has(li.id)),
    [filteredLineItems, selectedIdSet]
  );

  const allSelected = filteredLineItems.length > 0 && filteredLineItems.every(li => selectedIdSet.has(li.id));
  const isViewingSubsetOfOriginalReport =
    (activeEntityIds?.length ?? 0) > 0 && (activeEntityIds?.length ?? 0) < lineItems.length;

  const handleSearchTextChange = debounce((value: string) => {
    setState(prev => ({ ...prev, searchText: value }));
  }, 500);

  const handleSelectionChange = (items: LineItemRowData[]) => {
    setState(prev => ({ ...prev, selectedIds: items.map(item => item.id), userHasInteracted: true }));
  };

  const handleClear = () => {
    setState(prev => ({ ...prev, selectedIds: [], userHasInteracted: true }));
  };

  const handleSelectAllToggle = () => {
    if (allSelected) {
      setState(prev => ({ ...prev, selectedIds: [], userHasInteracted: true }));
      return;
    }
    const filteredIds = new Set(filteredLineItems.map(li => li.id));
    const othersAlreadySelected = effectiveSelectedIds.filter(id => !filteredIds.has(id));
    setState(prev => ({
      ...prev,
      selectedIds: [...othersAlreadySelected, ...filteredLineItems.map(li => li.id)],
      userHasInteracted: true
    }));
  };

  const handleBackToAllLineItems = () => {
    navigateToReport({ lineItemIds: undefined });
    onClose();
  };

  const handleSeeAggregatedReport = () => {
    const isSelectingAllLineItems = effectiveSelectedIds.length === lineItems.length;
    navigateToReport({ lineItemIds: isSelectingAllLineItems ? undefined : effectiveSelectedIds });
    onClose();
  };

  return (
    <ModalUI
      closeModalCallback={onClose}
      description={formatMessage({
        defaultMessage: 'Here are the line items selected for your aggregated report.',
        id: 'driveToStore.lineItemSelectionModal.description'
      })}
      leftButton={
        <ButtonUI
          id="cancel-button"
          onClick={onClose}
          text={formatMessage({ defaultMessage: 'Cancel', id: 'common.cancel' })}
          type="secondary"
        />
      }
      ref={modalRef}
      rightButton={
        <div className="lineItemSelectionModal__footerButtons">
          <ButtonUI
            disabled={!isViewingSubsetOfOriginalReport}
            id="back-to-all-line-items-button"
            onClick={handleBackToAllLineItems}
            text={formatMessage({
              defaultMessage: 'Back to all Line Items',
              id: 'driveToStore.lineItemSelectionModal.backToAll'
            })}
            type="secondary"
          />
          <ButtonUI
            disabled={effectiveSelectedIds.length === 0}
            id="see-aggregated-report-button"
            onClick={handleSeeAggregatedReport}
            text={formatMessage({
              defaultMessage: 'See aggregated report',
              id: 'driveToStore.lineItemSelectionModal.seeAggregatedReport'
            })}
            type="primary"
          />
        </div>
      }
      title={formatMessage({
        defaultMessage: 'Line Item aggregated report',
        id: 'driveToStore.lineItemSelectionModal.title'
      })}
      width="1252px"
    >
      <div className="lineItemSelectionModal__search">
        <InputSearch
          id="lineItemSelectionModal__inputSearch"
          lens
          name="lineItemSelectionModal__inputSearch"
          onChange={handleSearchTextChange}
          placeholder={formatMessage({
            defaultMessage: 'Search for a Line Item...',
            id: 'driveToStore.lineItemSelectionModal.searchPlaceholder'
          })}
          tiny
        />
      </div>
      <TaptapList<LineItemRowData>
        className="lineItemSelectionModal__table"
        data={filteredLineItems}
        defaultSelectedItems={defaultSelectedRows}
        id="lineItemSelectionModal"
        onSelectionChange={handleSelectionChange}
        selectionType="rowClick"
      >
        <TaptapList.Toolbar>
          <SelectionToolbar
            allSelected={allSelected}
            hasSelection={effectiveSelectedIds.length > 0}
            onClear={handleClear}
            onSelectAll={handleSelectAllToggle}
            resultsCount={filteredLineItems.length}
            selectedCount={effectiveSelectedIds.length}
          />
        </TaptapList.Toolbar>
        <TaptapList.ColumnsBar>
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colName"
            description={formatMessage({
              defaultMessage: 'Line Item',
              id: 'driveToStore.lineItemSelectionModal.columns.name'
            })}
            id="name"
          />
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colPoiCount"
            description={formatMessage({
              defaultMessage: 'POIs in LI',
              id: 'driveToStore.lineItemSelectionModal.columns.poiCount'
            })}
            id="poiCount"
          />
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colStatus"
            description={formatMessage({
              defaultMessage: 'Status',
              id: 'driveToStore.lineItemSelectionModal.columns.status'
            })}
            id="status"
          />
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colPricingModel"
            description={formatMessage({
              defaultMessage: 'Pricing Model',
              id: 'driveToStore.lineItemSelectionModal.columns.pricingModel'
            })}
            id="pricingModel"
          />
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colDuration"
            description={formatMessage({
              defaultMessage: 'Duration',
              id: 'driveToStore.lineItemSelectionModal.columns.duration'
            })}
            id="duration"
          />
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colLastUpdate"
            description={formatMessage({
              defaultMessage: 'Last update',
              id: 'driveToStore.lineItemSelectionModal.columns.lastUpdate'
            })}
            id="lastUpdate"
          />
          {/* biome-ignore lint: column IDs are semantic keys, not DOM ids */}
          <TaptapList.ColumnsBar.Column
            className="lineItemSelectionModal__colHeader lineItemSelectionModal__colBudgetSpend"
            description={formatMessage({
              defaultMessage: 'Budget spent',
              id: 'driveToStore.lineItemSelectionModal.columns.budgetSpend'
            })}
            id="budgetSpend"
          />
        </TaptapList.ColumnsBar>
        <TaptapList.RowsList<LineItemRowData>
          emptyStateIcon="fal fa-empty-set"
          emptyStateTitle={formatMessage({
            defaultMessage: 'There is no data for the current search.',
            id: 'driveToStore.lineItemSelectionModal.emptyState'
          })}
        >
          {itemsList =>
            itemsList.map(item => (
              <LineItemTableRow
                currencyInfo={currencyInfo}
                fullLineItem={fullLineItemById.get(item.id)}
                key={item.id}
                lineItem={item}
                userId={userId}
              />
            ))
          }
        </TaptapList.RowsList>
      </TaptapList>
    </ModalUI>
  );
}
